/**
 * Email Pipeline — orchestrator for the full email order extraction flow.
 *
 * Flow: SEARCH → FILTER → READ → CLASSIFY → PARSE → RESULTS
 *
 * All processing happens client-side (emails stay in browser).
 * LLM calls go through Strapi backend (Groq, free).
 * Only confirmed items are sent to the backend for import.
 */

import { getGmailToken } from './gmailAuth';
import { searchEmails, readEmails, buildQuery } from './gmailClient';
import { filterOrderEmails, buildDefaultOrderQuery } from './emailFilter';
import { classifyFashionEmails } from './emailClassifier';
import { parseOrderEmails } from './emailParser';
import { BACKEND_URL } from '../utils/config';

// Check if backend is v5
const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';
const apiPrefix = isV5 ? '/api' : '';

/**
 * Helper to interact with the backend API
 */
async function fetchScanHistory() {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) return [];
    
    const res = await fetch(`${BACKEND_URL}${apiPrefix}/email-scan-histories?_limit=1000`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!res.ok) return [];
    return await res.json();
  } catch (err) {
    console.warn('[Pipeline] Failed to fetch scan history:', err);
    return [];
  }
}

async function saveScanHistory(messageId, subject, isFashion, orderNumber = null) {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) return;
    
    await fetch(`${BACKEND_URL}${apiPrefix}/email-scan-histories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messageId,
        subject,
        isFashion,
        orderNumber
      })
    });
  } catch (err) {
    console.warn('[Pipeline] Failed to save scan history:', err);
  }
}

/**
 * Pipeline phases for progress reporting.
 */
export const PIPELINE_PHASES = {
  SEARCH: { key: 'search', label: 'Searching emails', icon: 'search' },
  FILTER: { key: 'filter', label: 'Filtering orders', icon: 'filter' },
  READ: { key: 'read', label: 'Reading emails', icon: 'mail' },
  CLASSIFY: { key: 'classify', label: 'Identifying fashion', icon: 'tag' },
  PARSE: { key: 'parse', label: 'Extracting items', icon: 'magic' },
  DONE: { key: 'done', label: 'Complete', icon: 'check' }
};

/**
 * Run the full email order extraction pipeline.
 *
 * @param {object} options
 * @param {object} options.dateRange - { after: 'YYYY-MM-DD', before: 'YYYY-MM-DD' }
 * @param {string} options.keywords - Optional custom search keywords
 * @param {boolean} options.useDefaultQuery - Use auto-generated order query (default: true)
 * @param {function} options.onPhase - Phase progress callback: (phase, detail)
 * @param {function} options.onBrainProgress - Model download/init progress callback
 * @returns {Promise<Array>} Parsed order items
 */
export async function runEmailPipeline({
  dateRange,
  keywords,
  useDefaultQuery = true,
  scanDepth = 100,
  onPhase,
  onBrainProgress
} = {}) {
  const token = getGmailToken();
  if (!token) throw new Error('Gmail not connected');

  console.log('[Pipeline] Starting with options:', { dateRange, keywords, useDefaultQuery });

  // Phase 1: SEARCH
  if (onPhase) onPhase(PIPELINE_PHASES.SEARCH, { message: 'Searching for orders...' });

  let searchQuery;
  if (keywords && keywords.trim()) {
    searchQuery = buildQuery({
      keywords: [keywords.trim()],
      timeRange: dateRange || {}
    });
  } else if (useDefaultQuery) {
    searchQuery = buildDefaultOrderQuery(dateRange);
  } else {
    searchQuery = buildQuery({ timeRange: dateRange || {} });
  }

  console.log('[Pipeline] Gmail search query:', searchQuery);

  const candidates = await searchEmails(token, searchQuery, scanDepth);

  console.log('[Pipeline] SEARCH → found', candidates.length, 'emails');
  if (candidates.length > 0) {
    console.log('[Pipeline] Sample candidates:', candidates.slice(0, 3).map(c => ({
      subject: c.subject, from: c.from, date: c.date
    })));
  }

  if (candidates.length === 0) {
    if (onPhase) onPhase(PIPELINE_PHASES.DONE, { message: 'No emails found' });
    return { items: [], status: 'SUCCESS' };
  }

  // Fetch scan history and filter out already processed emails
  if (onPhase) onPhase(PIPELINE_PHASES.SEARCH, { message: 'Checking previously scanned emails...' });
  const scanHistory = await fetchScanHistory();
  const scannedIds = new Set(scanHistory.map(h => h.messageId));
  
  const newCandidates = candidates.filter(c => !scannedIds.has(c.id));
  const skippedCount = candidates.length - newCandidates.length;
  
  console.log(`[Pipeline] SEARCH → found ${candidates.length} emails, skipped ${skippedCount} already scanned.`);
  
  if (newCandidates.length === 0) {
    if (onPhase) onPhase(PIPELINE_PHASES.DONE, { message: `All ${candidates.length} emails were already scanned.` });
    return { items: [], status: 'NO_NEW_EMAILS', scannedCount: candidates.length };
  }

  if (onPhase) onPhase(PIPELINE_PHASES.SEARCH, {
    message: `Found ${newCandidates.length} new emails (skipped ${skippedCount})`
  });

  // Phase 2: FILTER (deterministic scoring)
  if (onPhase) onPhase(PIPELINE_PHASES.FILTER, { message: 'Scoring for order relevance...' });

  // If user searched by keyword, be less strict with filtering
  const filtered = keywords ? newCandidates : filterOrderEmails(newCandidates);

  console.log('[Pipeline] FILTER → kept', filtered.length, 'of', newCandidates.length, 'emails');
  if (filtered.length > 0) {
    console.log('[Pipeline] Filtered sample:', filtered.slice(0, 3).map(f => ({
      subject: f.subject, from: f.from, score: f.orderScore
    })));
  }

  if (filtered.length === 0) {
    if (onPhase) onPhase(PIPELINE_PHASES.DONE, {
      message: 'No order confirmations found in those emails'
    });
    // Mark the dropped ones as not fashion to skip next time
    const dropped = newCandidates.filter(c => !filtered.find(f => f.id === c.id));
    for (const c of dropped) {
      await saveScanHistory(c.id, c.subject, false);
    }
    return [];
  }
  
  // Mark filtered out emails as non-fashion in history so we don't score them again
  const droppedByFilter = newCandidates.filter(c => !filtered.find(f => f.id === c.id));
  for (const c of droppedByFilter) {
    await saveScanHistory(c.id, c.subject, false);
  }

  if (onPhase) onPhase(PIPELINE_PHASES.FILTER, {
    message: `${filtered.length} likely order emails`
  });

  // Phase 3: READ (fetch full bodies + extract images)
  if (onPhase) onPhase(PIPELINE_PHASES.READ, {
    message: `Reading ${Math.min(filtered.length, 25)} emails...`
  });

  const readCandidates = await readEmails(token, filtered, 25);

  console.log('[Pipeline] READ → got', readCandidates.length, 'with body');
  readCandidates.forEach((r, i) => {
    console.log(`[Pipeline] READ[${i}]: subject="${r.subject}", bodyLen=${r.cleanedBody?.length || 0}, images=${r.imageUrls?.length || 0}`);
  });

  // Phase 4: CLASSIFY (LLM determines if fashion order)
  if (onPhase) onPhase(PIPELINE_PHASES.CLASSIFY, { message: 'Identifying fashion orders...' });

  const fashionEmails = await classifyFashionEmails(readCandidates);

  console.log('[Pipeline] CLASSIFY → fashion:', fashionEmails.length, 'of', readCandidates.length);
  
  // Save non-fashion emails to history
  const nonFashionEmails = readCandidates.filter(r => !fashionEmails.find(f => f.id === r.id));
  for (const nf of nonFashionEmails) {
    await saveScanHistory(nf.id, nf.subject, false);
  }

  fashionEmails.forEach((f, i) => {
    console.log(`[Pipeline] CLASSIFY[${i}]: subject="${f.subject}", isFashion=${f.classification?.isFashion}, confidence=${f.classification?.confidence}, reason="${f.classification?.reason}"`);
  });

  if (fashionEmails.length === 0) {
    if (onPhase) onPhase(PIPELINE_PHASES.DONE, {
      message: 'No fashion orders found in those emails'
    });
    return [];
  }

  if (onPhase) onPhase(PIPELINE_PHASES.CLASSIFY, {
    message: `${fashionEmails.length} fashion order${fashionEmails.length !== 1 ? 's' : ''} found`
  });

  // Phase 5: PARSE (LLM extracts structured item data)
  if (onPhase) onPhase(PIPELINE_PHASES.PARSE, { message: 'Extracting items with AI...' });

  const items = await parseOrderEmails(fashionEmails, onBrainProgress);

  console.log('[Pipeline] PARSE → extracted', items.length, 'items');
  
  // Group items by email to save history
  const itemsByEmailId = {};
  items.forEach(item => {
    if (!itemsByEmailId[item.emailId]) itemsByEmailId[item.emailId] = [];
    itemsByEmailId[item.emailId].push(item);
  });
  
  // Save fashion emails to history
  for (const f of fashionEmails) {
    const extracted = itemsByEmailId[f.id] || [];
    const orderNumber = extracted.length > 0 ? extracted[0].orderNumber : null;
    await saveScanHistory(f.id, f.subject, true, orderNumber);
  }

  items.forEach((item, i) => {
    console.log(`[Pipeline] ITEM[${i}]:`, { name: item.itemName, brand: item.brand, category: item.category, imageUrl: item.imageUrl?.slice(0, 60) });
  });

  // Phase 6: DONE
  if (onPhase) onPhase(PIPELINE_PHASES.DONE, {
    message: items.length > 0
      ? `Found ${items.length} item${items.length === 1 ? '' : 's'}`
      : 'No fashion items found in the emails'
  });

  if (items.length === 0) {
    return { items: [], status: 'NO_FASHION_EMAILS', scannedCount: newCandidates.length };
  }

  return { items, status: 'SUCCESS', scannedCount: newCandidates.length };
}
